import { AxiosResponse } from "axios";
import {
  SET_ORGANIZATION_SETTINGS,
  SET_ORGANIZATION_SETTING,
  SET_ERRORMSG,
} from "@/store/mutation-types";
import {
  CLEAR_ORGANIZATION_SETTING_ISLOADING,
  DELETE_ORGANIZATION_SETTING,
  EDIT_ORGANIZATION_SETTING,
  GET_ORGANIZATION_SETTING,
  GET_ORGANIZATION_SETTINGS,
  REGISTER_ORGANIZATION_SETTING,
} from "@/store/action-types";
import { StateObject } from "@/dtos/State.dto";
import OrganizationSetting from "@/services/organization/organization-setting";
import { Module } from "vuex";
import {
  EditOrganizationSetting,
  RegisterOrganizationSetting,
  GetOrganizationSetting,
} from "@/dtos/OrganizationSetting.dto";

const organizationSetting: Module<any, {}> = {
  state: {
    organizationSetting: {
      data: null,
      isLoading: false,
    },
    organizationSettings: {
      data: null,
      isLoading: false,
    },
  },
  getters: {
    organizationSetting: (state: { organizationSetting: any }) => state.organizationSetting,
    organizationSettings: (state: { organizationSettings: any }) => state.organizationSettings,
  },
  mutations: {
    [SET_ORGANIZATION_SETTING]: (
      state: { organizationSetting: StateObject },
      payload: { data: null; status: any; isLoading: any }
    ) => {
      if (payload.data != null) state.organizationSetting.data = payload.data;
      state.organizationSetting.isLoading = payload.isLoading;
    },
    [SET_ORGANIZATION_SETTINGS]: (
      state: { organizationSettings: StateObject },
      payload: { data: null; status: any; isLoading: any }
    ) => {
      if (payload.data != null) state.organizationSettings.data = payload.data;
      state.organizationSettings.isLoading = payload.isLoading;
    },
  },
  actions: {
    [REGISTER_ORGANIZATION_SETTING]: async ({ commit }: any, payload: any) => {
      commit(SET_ORGANIZATION_SETTING, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await OrganizationSetting.registerOrganizationSetting(
          payload as RegisterOrganizationSetting
        );

        if (result) {
          if (result.data.status) {
            commit(SET_ORGANIZATION_SETTING, {
              data: result.data.data,
              isLoading: false,
            });
            return true;
          }
          commit(SET_ORGANIZATION_SETTING, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_ORGANIZATION_SETTING, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_ORGANIZATION_SETTING, { data: null, isLoading: false });

        // Handle timeout specifically
        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please check your connection and try again.");
        } else {
          commit(
            SET_ERRORMSG,
            error.response?.data?.message || "Failed to register organization setting"
          );
        }
        return false;
      }
    },
    [GET_ORGANIZATION_SETTING]: async ({ commit }: any, payload: any) => {
      commit(SET_ORGANIZATION_SETTING, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await OrganizationSetting.getOrganizationSettings(
          payload as GetOrganizationSetting
        );

        if (result) {
          if (result.data.status) {
            commit(SET_ORGANIZATION_SETTING, {
              data: result.data.data[0],
              isLoading: false,
            });
            return result.data.data;
          }
          commit(SET_ORGANIZATION_SETTING, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_ORGANIZATION_SETTING, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_ORGANIZATION_SETTING, { data: null, isLoading: false });

        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(
            SET_ERRORMSG,
            "Request timed out. The server is taking too long to respond. Please try again later."
          );
        } else if (error.response?.status === 500) {
          commit(
            SET_ERRORMSG,
            "Server error occurred while loading organization settings. Please try again later."
          );
        } else if (error.response?.status === 404) {
          commit(SET_ERRORMSG, "Organization settings not found. Please contact support.");
        } else if (!error.response) {
          commit(
            SET_ERRORMSG,
            "Network error. Please check your internet connection and try again."
          );
        } else {
          commit(
            SET_ERRORMSG,
            error.response?.data?.message ||
              "Failed to load organization setting. Please try again."
          );
        }

        return false;
      }
    },
    [EDIT_ORGANIZATION_SETTING]: async ({ commit }: any, payload: any) => {
      commit(SET_ORGANIZATION_SETTING, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await OrganizationSetting.editOrganizationSetting(
          payload as EditOrganizationSetting
        );

        if (result) {
          if (result.data.status) {
            commit(SET_ORGANIZATION_SETTING, {
              data: result.data.data,
              isLoading: false,
            });
            return true;
          }
          commit(SET_ORGANIZATION_SETTING, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_ORGANIZATION_SETTING, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_ORGANIZATION_SETTING, { data: null, isLoading: false });

        // Handle timeout specifically
        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please check your connection and try again.");
        } else {
          commit(
            SET_ERRORMSG,
            error.response?.data?.message || "Failed to edit organization setting"
          );
        }
        return false;
      }
    },
    [DELETE_ORGANIZATION_SETTING]: async ({ commit }: any, payload: string) => {
      commit(SET_ORGANIZATION_SETTING, {
        data: { id: payload },
        isLoading: true,
      });

      try {
        const result: AxiosResponse = await OrganizationSetting.deleteOrganizationSetting(payload);

        if (result) {
          if (result.data.status) {
            commit(SET_ORGANIZATION_SETTING, {
              data: result.data.data,
              isLoading: false,
            });
            return true;
          }
          commit(SET_ORGANIZATION_SETTING, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_ORGANIZATION_SETTING, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_ORGANIZATION_SETTING, { data: null, isLoading: false });

        // Handle timeout specifically
        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please check your connection and try again.");
        } else {
          commit(
            SET_ERRORMSG,
            error.response?.data?.message || "Failed to delete organization setting"
          );
        }
        return false;
      }
    },
    [CLEAR_ORGANIZATION_SETTING_ISLOADING]: ({ commit }) => {
      commit(SET_ORGANIZATION_SETTING, { isLoading: false });
      commit(SET_ORGANIZATION_SETTINGS, { isLoading: false });
    },
  },
};

export default organizationSetting;
